"use client";

import type { ReportingProps } from "@/components/report-dialog";
import { useHandyoutEvents } from "@/hooks/use-handyout-events";
import { fetchApi, mutateApi } from "@/lib/api-fetch";
import { queryKeys } from "@/lib/query-keys";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import {
  type ModerationComment,
  type ModerationReport,
} from "./use-comment-moderation";
import { type Profile, useProfileQuery } from "./use-profile";

export interface CommentRow {
  id: string;
  text: string;
  author: string;
  anonymous?: boolean;
  avatar_url?: string;
  time: string;
  created_at?: string;
  session_id?: string;
  slide_id?: string;
  parent_id?: string | null;
  author_type?: string;
  mailing?: boolean;
  likes?: number;
  replies?: CommentRow[];
}

interface CommentsRawRow {
  id: string;
  text: string;
  created_at?: string | null;
  time?: string | null;
  session_id?: string | null;
  slide_id?: string | null;
  parent_id?: string | null;
  author_type?: string | null;
  sessions?: { name?: string | null } | null;
  profile?: {
    first_name?: string | null;
    last_name?: string | null;
    avatar_url?: string | null;
  } | null;
  mailing?: boolean | null;
  likes?: number | null;
  replies?: CommentsRawRow[] | null;
}

function mapRow(row: CommentsRawRow): CommentRow {
  const normalizedTime = row.time ?? row.created_at ?? new Date().toISOString();
  const presenterName = row.profile
    ? [row.profile.first_name, row.profile.last_name]
        .filter(Boolean)
        .join(" ")
        .trim()
    : "";
  const presenter = row.author_type === "presenter" && Boolean(presenterName);
  const sessionName = row.sessions?.name;
  const author = presenter ? presenterName : (sessionName ?? "Anonymous");
  return {
    id: row.id,
    text: row.text,
    author,
    anonymous: !presenter && sessionName == null,
    avatar_url: row.profile?.avatar_url ?? undefined,
    time: normalizedTime,
    created_at: row.created_at ?? normalizedTime,
    session_id: row.session_id ?? undefined,
    slide_id: row.slide_id ?? undefined,
    parent_id: row.parent_id ?? null,
    author_type: row.author_type ?? undefined,
    mailing: Boolean(row.mailing),
    likes: typeof row.likes === "number" ? row.likes : undefined,
    replies: row.replies ? row.replies.map(mapRow) : [],
  };
}

async function fetchComments(slideId: string): Promise<CommentRow[]> {
  const rows = await fetchApi<CommentsRawRow[]>(
    `/api/comments?slide_id=${slideId}`,
  ).catch(() => null);
  return (rows ?? []).map(mapRow);
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function prefetchCommentsQuery(
  queryClient: QueryClient,
  slideId: string,
) {
  if (!slideId || !UUID_RE.test(slideId)) return Promise.resolve();
  return queryClient.prefetchQuery({
    queryKey: queryKeys.comments.bySlide(slideId),
    queryFn: () => fetchComments(slideId),
  });
}

function isAddingComment(queryClient: QueryClient, slideId: string) {
  return (
    queryClient.isMutating({ mutationKey: queryKeys.comments.add(slideId) }) > 0
  );
}

/**
 * While the reader's own comment is still in flight, a `comments` event must
 * not refetch that slide's threads: the server list would not contain the new
 * comment yet and the optimistic row would vanish and reappear. The add
 * mutation reconciles the threads itself once it settles.
 */
export function useCommentsQuery(slideId: string, handyoutId?: string) {
  const queryClient = useQueryClient();
  const enabled = !!slideId && UUID_RE.test(slideId);

  const query = useQuery({
    queryKey: queryKeys.comments.bySlide(slideId),
    queryFn: () => fetchComments(slideId),
    enabled,
    placeholderData: (previous) => previous,
  });

  useHandyoutEvents(handyoutId, {
    onEvent: (event) => {
      if (event.type !== "comments") return;
      if (!event.slide_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.comments.all,
          predicate: (query) => {
            const [, kind, id] = query.queryKey;
            return !(
              kind === "bySlide" &&
              typeof id === "string" &&
              isAddingComment(queryClient, id)
            );
          },
        });
        return;
      }
      if (!isAddingComment(queryClient, event.slide_id)) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.comments.bySlide(event.slide_id),
        });
      }
      // A like emits a `comments` event too, and the liked ids are a sibling key
      // of the threads, so the per-slide branch has to name both.
      queryClient.invalidateQueries({
        queryKey: queryKeys.comments.likes(event.slide_id),
      });
    },
    onResync: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.comments.all });
    },
  }, { audience: "public" });

  return query;
}

function toIdList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((id): id is string => typeof id === "string")
    : [];
}

async function fetchCommentLikes(slideId: string): Promise<string[]> {
  const data = await fetchApi<{ liked_comment_ids?: unknown }>(
    `/api/comments/likes?slide_id=${slideId}`,
  ).catch(() => null);
  return toIdList(data?.liked_comment_ids);
}

export function useCommentLikesQuery(slideId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.comments.likes(slideId),
    queryFn: () => fetchCommentLikes(slideId),
    enabled: enabled && !!slideId && UUID_RE.test(slideId),
    placeholderData: (previous) => previous,
  });
}

interface CommentLikeResult {
  comment_id?: string;
  liked?: boolean;
  liked_comment_ids?: unknown;
  likes?: number | null;
}

function withLikeCount(
  rows: CommentRow[],
  commentId: string,
  likes: number,
): CommentRow[] {
  return rows.map((row) => {
    if (row.id === commentId) return { ...row, likes };
    const original = row.replies;
    if (!original?.length) return row;
    const replies = withLikeCount(original, commentId, likes);
    return replies.some((r, i) => r !== original[i]) ? { ...row, replies } : row;
  });
}

const NO_PENDING_LIKES: ReadonlySet<string> = new Set();

/**
 * The backend toggle is a read-modify-write on the session row, so two in-flight
 * toggles of the same comment can cancel each other out. A click on a comment
 * whose toggle has not settled yet is therefore dropped, per comment id, and the
 * liked ids are taken from the response instead of guessed optimistically.
 *
 * The ref is the guard and the state is only for rendering: a ref cannot trigger
 * the re-render the button needs, and state read inside the guard would be the
 * value captured when the callback was built.
 */
export function useToggleCommentLikeMutation(slideId: string) {
  const queryClient = useQueryClient();
  const inFlight = useRef<Set<string>>(new Set());
  const [pendingLikeIds, setPendingLikeIds] =
    useState<ReadonlySet<string>>(NO_PENDING_LIKES);

  const { mutate } = useMutation({
    mutationFn: (commentId: string) =>
      mutateApi<CommentLikeResult | null>("POST", "/api/comments/like", {
        comment_id: commentId,
      }),
    onSuccess: (result, commentId) => {
      if (!result) return;
      queryClient.setQueryData<string[]>(
        queryKeys.comments.likes(slideId),
        toIdList(result.liked_comment_ids),
      );
      const likes = result.likes;
      if (typeof likes !== "number" || result.comment_id !== commentId) return;
      queryClient.setQueryData<CommentRow[]>(
        queryKeys.comments.bySlide(slideId),
        (prev) => (prev ? withLikeCount(prev, commentId, likes) : prev),
      );
    },
    onSettled: (_data, _error, commentId) => {
      inFlight.current.delete(commentId);
      setPendingLikeIds(new Set(inFlight.current));
      if (isAddingComment(queryClient, slideId)) return;
      queryClient.invalidateQueries({
        queryKey: queryKeys.comments.bySlide(slideId),
      });
    },
  });

  const toggleLike = useCallback(
    (commentId: string) => {
      if (inFlight.current.has(commentId)) return;
      inFlight.current.add(commentId);
      setPendingLikeIds(new Set(inFlight.current));
      mutate(commentId);
    },
    [mutate],
  );

  return { toggleLike, pendingLikeIds };
}

export type CommentDraft = string | { text: string; parentId?: string };

/**
 * The presenter's name and avatar on the optimistic row come from the profile
 * cache only. Whoever is signed in already has it loaded (the dashboard shell
 * reads it); an anonymous reader on /p has none, and reading the cache instead
 * of subscribing keeps that reader from requesting /api/profiles/me at all.
 * Whether the comment really counts as the presenter's is decided by the
 * backend, and the refetch on settle shows that verdict.
 *
 * Several comments can be in flight at once, so a failure removes only its own
 * optimistic row, and only the last comment to settle refetches the threads.
 *
 * A draft may carry its own parent, which wins over the hook's `parentId`: a
 * composer that clears its reply target on submit must not depend on that
 * state still being in place when the mutation is built.
 */
export function useAddCommentMutation({
  slideId,
  parentId,
}: {
  slideId: string;
  parentId?: string;
}) {
  const queryClient = useQueryClient();
  const mutationKey = queryKeys.comments.add(slideId);
  const resolveDraft = (draft: CommentDraft) =>
    typeof draft === "string"
      ? { text: draft, parentId }
      : {
          text: draft.text,
          parentId: "parentId" in draft ? draft.parentId : parentId,
        };

  return useMutation({
    mutationKey,
    mutationFn: (draft: CommentDraft) => {
      const { text, parentId: target } = resolveDraft(draft);
      return mutateApi<CommentRow | null>("POST", "/api/comments", {
        slide_id: slideId,
        text,
        parent_id: target,
      });
    },
    onMutate: async (draft) => {
      const { text, parentId: target } = resolveDraft(draft);
      await queryClient.cancelQueries({
        queryKey: queryKeys.comments.bySlide(slideId),
      });

      const profile = queryClient.getQueryData<Profile | null>(
        queryKeys.profile.me(),
      );
      const presenterName = profile
        ? [profile.first_name, profile.last_name]
            .filter(Boolean)
            .join(" ")
            .trim()
        : "";
      const nowISO = new Date().toISOString();
      const optimistic: CommentRow = {
        id: `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        text,
        author: presenterName || "You",
        avatar_url: profile?.avatar_url ?? undefined,
        time: nowISO,
        created_at: nowISO,
        parent_id: target ?? null,
        author_type: profile ? "presenter" : "audience",
        replies: [],
      };

      queryClient.setQueryData<CommentRow[]>(
        queryKeys.comments.bySlide(slideId),
        (prev) => {
          if (!target) {
            return [optimistic, ...(prev ?? [])];
          }
          return (prev ?? []).map((c) =>
            c.id === target
              ? { ...c, replies: [optimistic, ...(c.replies ?? [])] }
              : c,
          );
        },
      );

      return { optimisticId: optimistic.id };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      const dropOptimistic = (rows: CommentRow[]): CommentRow[] =>
        rows
          .filter((c) => c.id !== context.optimisticId)
          .map((c) =>
            c.replies?.some((r) => r.id === context.optimisticId)
              ? {
                  ...c,
                  replies: c.replies.filter(
                    (r) => r.id !== context.optimisticId,
                  ),
                }
              : c,
          );
      queryClient.setQueryData<CommentRow[]>(
        queryKeys.comments.bySlide(slideId),
        (prev) => (prev ? dropOptimistic(prev) : prev),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.comments.summaries });
      if (queryClient.isMutating({ mutationKey }) > 1) return;
      queryClient.invalidateQueries({
        queryKey: queryKeys.comments.bySlide(slideId),
      });
    },
  });
}

export function useAddReportMutation({ slideId }: { slideId: string }) {
  const queryClient = useQueryClient();
  const { data: profile } = useProfileQuery();
  const moderationKey = queryKeys.comments.moderation(slideId);

  return useMutation({
    mutationFn: ({ commentId, reason }: ReportingProps) =>
      mutateApi<unknown>("POST", "/api/comments/report", {
        comment_id: commentId,
        reporter_user_id: profile?.user_id,
        reason: {
          title: reason.title,
          report_reason_description: reason.reportReasonDescription,
          report_reason: reason.reportReason,
        },
      }),

    onMutate: async ({ commentId, reason }) => {
      await queryClient.cancelQueries({ queryKey: moderationKey });

      const reporterName = profile
        ? [profile.first_name, profile.last_name]
            .filter(Boolean)
            .join(" ")
            .trim()
        : "";
      const optimistic: ModerationReport = {
        reason: reason.reportReason,
        description: reason.reportReasonDescription?.trim() || null,
        reported_at: new Date().toISOString(),
        reporter_name: reporterName || null,
      };

      queryClient.setQueryData<ModerationComment[]>(moderationKey, (prev) =>
        prev?.map((entry) =>
          entry.id === commentId
            ? { ...entry, reports: [...entry.reports, optimistic] }
            : entry,
        ),
      );

      return { commentId, optimistic };
    },

    onError: (_err, _report, context) => {
      if (!context) return;
      queryClient.setQueryData<ModerationComment[]>(moderationKey, (prev) =>
        prev?.map((entry) =>
          entry.id === context.commentId
            ? {
                ...entry,
                reports: entry.reports.filter(
                  (r) => r !== context.optimistic,
                ),
              }
            : entry,
        ),
      );
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: moderationKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.comments.summaries });
      if (isAddingComment(queryClient, slideId)) return;
      queryClient.invalidateQueries({
        queryKey: queryKeys.comments.bySlide(slideId),
      });
    },
  });
}
